import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import QuickAddBar from './QuickAddBar';
import TaskRow from './TaskRow';
import UndoRow from './UndoRow';
import TaskCalendar from './TaskCalendar';
import TimeInput from './TimeInput';
import SettingsModal, {
  loadCalendarSettings,
  saveCalendarSettingsLocal,
} from './SettingsModal';
import {
  isConsented,
  hasLiveToken,
  refreshOnGesture,
  refreshTokenIfNeeded,
  connectGoogle,
  preloadGoogle,
  setFallbackLoginHint,
} from './googleCalendar';
import {
  importFromGoogle,
  exportToGoogle,
  flushDeletes,
  importWindow,
  exportWindow,
} from './calendarSync';
import { HISTORY_YEARS, EXPORT_DAYS_BACK } from './googleConfig';
import { signInWithGoogle, signOutUser, onAuthChange } from './firebase';
import { shortcutOf } from './keyboard';
import NoteModal from './NoteModal';
import {
  loadLocalDay,
  saveLocalDay,
  listLocalDates,
  migrateLocalStorage,
  fetchRemoteDay,
  saveRemoteDay,
  fetchRemoteRange,
  subscribeRemoteDay,
  migrateRemoteIfNeeded,
  mergeTasks,
  needsPush,
  purgeTombstones,
  loadPendingDeletes,
  savePendingDeletes,
  addPendingDelete,
  removePendingDelete,
  fetchCalendarPrefs,
  saveCalendarPrefs,
  subscribeCalendarPrefs,
  fetchOldestTaskDate,
} from './taskStore';
import {
  toDateStr,
  dateStrToMidnight,
  formatDateLabel,
  fmtHM,
  minutesToHM,
  addMinutesToDate,
  estToNum,
  remainingMinutesOf,
  secToClock,
  diffSeconds,
  isoToHM,
} from './timeUtils';

let idCounter = 0;
const newId = () => Date.now() * 1000 + (idCounter++ % 1000);

const SAVE_DELAY_MS = 1000;
const alive = (t) => !t.deletedAt;

const signature = (list = []) =>
  list
    .map((t) => `${t.id}:${t.updatedAt || 0}:${t.deletedAt || 0}`)
    .sort()
    .join('|');

/** 期間内の日付を並べる */
const datesBetween = (fromStr, toStr) => {
  const out = [];
  const cur = dateStrToMidnight(fromStr);
  const end = dateStrToMidnight(toStr);
  while (cur <= end) {
    out.push(toDateStr(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
};

/** "HH:mm" を1分戻す。00:00 は 00:00 のまま */
const minusOneMinute = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  const total = h * 60 + m - 1;
  if (total < 0) return '00:00';
  const nh = Math.floor(total / 60);
  const nm = total % 60;
  return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
};

/**
 * 調べもの用の記録。原因が分かったら false にする。
 * ブラウザのコンソール（F12）に、読み書きの結果が出る。
 */
const DEBUG = true;
const log = (...a) => DEBUG && console.log('%c[MyTaskLog]', 'color:#5a6bd8;font-weight:bold', ...a);
const alive2 = (list = []) => list.filter((t) => !t.deletedAt);

function TaskManager() {
  const [days, setDays] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  // メモの画面を開いている対象。タスクのid、または 'quickadd'
  const [noteTarget, setNoteTarget] = useState(null);
  const [ready, setReady] = useState(false);

  const [futureBase, setFutureBase] = useState('00:00');

  // タスク名の入力欄。Ctrl/Cmd + K で移動するときに使う
  const quickNameRef = useRef(null);
  // 追加バーの「追加」「すぐ開始」を、画面のどこからでも呼べるようにする
  const quickActionsRef = useRef(null);

  const [user, setUser] = useState(undefined);
  const [saveState, setSaveState] = useState('idle');
  const [historyState, setHistoryState] = useState({ busy: false, done: false, error: '' });
  const [calendarStale, setCalendarStale] = useState(false);

  const [gcalSettings, setGcalSettings] = useState(loadCalendarSettings);
  const [syncState, setSyncState] = useState({ busy: false, lastSyncAt: null, error: '' });

  /**
   * 取り消し待ちのタスク。複数を同時に持てるようにしてある。
   * { [taskId]: { name, date, task } }
   */
  const [undoMap, setUndoMap] = useState({});

  // 最古のタスク日付を計算（カレンダーの過去日付表示用）
  const [oldestTaskDate, setOldestTaskDate] = useState(null);

  const daysRef = useRef({});
  const userRef = useRef(undefined);
  const settingsRef = useRef(gcalSettings);
  const dirtyRef = useRef(new Set());
  const loadedRef = useRef(new Set());
  const saveTimerRef = useRef(null);
  const prevUidRef = useRef('__init__');

  useEffect(() => {
    userRef.current = user;
    // カレンダーのアカウントをまだ覚えていないときの代用に、ログイン中のメールを渡す
    setFallbackLoginHint(user ? user.email : '');
  }, [user]);
  useEffect(() => { settingsRef.current = gcalSettings; }, [gcalSettings]);

  useEffect(() => { preloadGoogle(); }, []);

  /**
   * ログインしている人のid。
   * 同じ人なら文字列も同じなので、下の useEffect が何度も走らない。
   */
  const authUid = user ? user.uid : null;

  /**
   * 最古のタスク日付を調べる（カレンダーで過去をどこまで開けるかに使う）。
   *
   * ここは authUid だけを見る。
   * 以前は days も見ていたため、タスクを1件書き換えるたびに
   * Firestore へ問い合わせが飛んでいた。
   * 手元の日付は daysRef から読むので、days を見る必要はない。
   */
  useEffect(() => {
    if (!authUid) {
      setOldestTaskDate(null);
      return;
    }

    let cancelled = false;

    const fetchOldest = async () => {
      try {
        const oldest = await fetchOldestTaskDate(authUid);
        if (cancelled) return;
        if (oldest) {
          setOldestTaskDate(oldest);
          return;
        }
      } catch (error) {
        console.warn('最古の日付を取れませんでした:', error.message);
        if (cancelled) return;
      }

      // 手元にある最古の日付
      const local = daysRef.current;
      const dates = Object.keys(local)
        .filter((d) => (local[d] || []).some((t) => alive(t)))
        .sort();
      if (dates.length > 0) {
        setOldestTaskDate(dates[0]);
        return;
      }

      // それも無ければ、今日から1年前
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      setOldestTaskDate(toDateStr(oneYearAgo));
    };

    fetchOldest();

    return () => {
      cancelled = true;
    };
  }, [authUid]);

  const [minuteStamp, setMinuteStamp] = useState(() => {
    const d = new Date();
    return `${toDateStr(d)} ${fmtHM(d)}`;
  });

  useEffect(() => {
    const id = setInterval(() => {
      const d = new Date();
      const stamp = `${toDateStr(d)} ${fmtHM(d)}`;
      setMinuteStamp((prev) => (prev === stamp ? prev : stamp));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const todayStr = minuteStamp.split(' ')[0];
  const dateStr = toDateStr(selectedDate);
  const isPast = dateStr < todayStr;
  const isFuture = dateStr > todayStr;
  const readOnly = isPast || isFuture;

  /**
   * 昨日の日付。
   * 記録そのもの（時刻・見積）は直せないが、
   * 名前とメモだけは翌日に書き足せるようにしてある。
   */
  const yesterdayStr = useMemo(() => {
    const d = dateStrToMidnight(todayStr);
    d.setDate(d.getDate() - 1);
    return toDateStr(d);
  }, [todayStr]);

  const isYesterday = dateStr === yesterdayStr;

  /**
   * 名前とメモを直せる、いちばん古い日。
   * 書き出しの範囲と同じにしてある。
   * ここより前を直しても Googleカレンダーには届かないため。
   */
  const editableFromStr = useMemo(() => {
    const d = dateStrToMidnight(todayStr);
    d.setDate(d.getDate() - EXPORT_DAYS_BACK);
    return toDateStr(d);
  }, [todayStr]);

  /**
   * 名前とメモを直せるか。
   * 今日と、EXPORT_DAYS_BACK 日前までの過去日。
   * 未来日は readOnly なので、過去日かどうかも併せて見る。
   */
  const canEditNotes = !readOnly || (isPast && dateStr >= editableFromStr);

  useEffect(() => {
    setFutureBase('00:00');
  }, [dateStr]);

  // 日を移ったら取り消しは打ち切る
  useEffect(() => {
    setUndoMap({});
  }, [dateStr]);

  // ==========================================================
  // カレンダーの選択（端末間で共有する）
  // ==========================================================
  const applyCalendarSettings = useCallback((next) => {
    setGcalSettings(next);
    settingsRef.current = next;
    saveCalendarSettingsLocal(next);
    const uid = userRef.current ? userRef.current.uid : null;
    if (uid) saveCalendarPrefs(uid, next);
  }, []);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    (async () => {
      const remote = await fetchCalendarPrefs(user.uid);
      if (cancelled) return;

      if (remote) {
        const next = {
          inputCalendarIds: remote.inputCalendarIds,
          outputCalendarId: remote.outputCalendarId,
        };
        setGcalSettings(next);
        settingsRef.current = next;
        saveCalendarSettingsLocal(next);
      } else {
        const local = settingsRef.current;
        const hasAny =
          (local.inputCalendarIds || []).length > 0 || local.outputCalendarId;
        if (hasAny) saveCalendarPrefs(user.uid, local);
      }
    })();

    const unsub = subscribeCalendarPrefs(user.uid, (remote) => {
      const next = {
        inputCalendarIds: remote.inputCalendarIds,
        outputCalendarId: remote.outputCalendarId,
      };
      setGcalSettings(next);
      settingsRef.current = next;
      saveCalendarSettingsLocal(next);
    });

    return () => {
      cancelled = true;
      unsub();
    };
  }, [user]);

  // ==========================================================
  // 保存
  // ==========================================================
  const flushSaves = useCallback(async () => {
    const targets = [...dirtyRef.current];
    if (targets.length === 0) return;
    dirtyRef.current.clear();

    const u = userRef.current;
    const dm = daysRef.current;

    targets.forEach((d) => saveLocalDay(d, dm[d] || []));
    if (!u) return;

    try {
      setSaveState('saving');
      targets.forEach((d) => log('保存', d, `${alive2(dm[d] || []).length}件`));
      await Promise.all(targets.map((d) => saveRemoteDay(u.uid, d, dm[d] || [])));
      setSaveState('idle');
    } catch (e) {
      console.warn('保存に失敗:', e.message);
      targets.forEach((d) => dirtyRef.current.add(d));
      setSaveState('error');
    }
  }, []);

  const scheduleSave = useCallback(
    (immediate) => {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(flushSaves, immediate ? 0 : SAVE_DELAY_MS);
    },
    [flushSaves]
  );

  /**
   * 通信が戻ったら、まだ送れていない記録を送る。
   *
   * Firestoreのキャッシュを使わない作りにしたので、
   * 回線が戻ったときに自動で送り直す仕組みがない。
   * 送れなかった日は「未保存」の印が残っているので、ここで送る。
   */
  useEffect(() => {
    const onOnline = () => {
      if (dirtyRef.current.size === 0) return;
      log('通信が戻ったので送ります', [...dirtyRef.current]);
      scheduleSave(true);
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [scheduleSave]);

  const commit = useCallback(
    (partial, { markDirty = true, immediate = false, replace = false } = {}) => {
      const dates = Object.keys(partial);
      if (dates.length === 0) return;

      const cur = daysRef.current;
      const next = { ...cur };
      const changedDates = [];

      dates.forEach((d) => {
        const incoming = partial[d] || [];
        const computed = purgeTombstones(
          replace ? incoming : mergeTasks(cur[d] || [], incoming)
        );
        if (cur[d] && signature(computed) === signature(cur[d])) {
          next[d] = cur[d];
          return;
        }
        next[d] = computed;
        changedDates.push(d);
      });

      if (changedDates.length === 0) return;

      daysRef.current = next;
      setDays(next);

      if (markDirty) {
        changedDates.forEach((d) => dirtyRef.current.add(d));
        scheduleSave(immediate);
      }
    },
    [scheduleSave]
  );

  const updateDay = useCallback(
    (d, updater, immediate = false) => {
      const nextList = updater(daysRef.current[d] || []);
      commit({ [d]: nextList }, { immediate, replace: true });
    },
    [commit]
  );

  useEffect(() => {
    const onHide = () => {
      if (dirtyRef.current.size > 0) flushSaves();
    };
    const onVis = () => {
      if (document.visibilityState === 'hidden') onHide();
    };
    window.addEventListener('pagehide', onHide);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [flushSaves]);

  // ==========================================================
  // 読み込み
  // ==========================================================
  useEffect(() => onAuthChange((u) => setUser(u || null)), []);

  const ensureDay = useCallback(
    async (d) => {
      if (loadedRef.current.has(d)) return;
      loadedRef.current.add(d);

      const local = loadLocalDay(d);
      const u = userRef.current;

      if (!u) {
        commit({ [d]: local }, { markDirty: false });
        return;
      }

      try {
        const remote = await fetchRemoteDay(u.uid, d);
        log(
          '読み込み', d,
          '/ uid', u.uid,
          '/ 手元', `${alive2(local).length}件(全${local.length})`,
          '/ Firestore', remote === null ? 'なし' : `${alive2(remote).length}件(全${remote.length})`
        );
        commit({ [d]: local }, { markDirty: false });
        commit({ [d]: remote || [] }, { markDirty: false });
        log('読み込み後', d, `${alive2(daysRef.current[d] || []).length}件`);
        if (needsPush(daysRef.current[d] || [], remote || [])) {
          dirtyRef.current.add(d);
          scheduleSave(false);
        }
      } catch (e) {
        /**
         * 読めなかった日は「読み込み済み」から外す。
         * 外さないと、日付を行き来しても二度と読み直さないので、
         * その日だけ空のまま戻らなくなる。
         */
        loadedRef.current.delete(d);
        console.warn('読み込み失敗:', e.message);
        commit({ [d]: local }, { markDirty: false });
      }
    },
    [commit, scheduleSave]
  );

  /**
   * 開いている日を、もう一度Firestoreから読み直す。
   *
   * 何かの拍子に取りこぼしたとき、日付を行き来しても
   * 「読み込み済み」の控えがあるせいで直らない。
   * その控えを外してから読み直す。
   */
  const reloadDay = useCallback(
    async (d) => {
      log('読み直し', d);
      loadedRef.current.delete(d);
      await ensureDay(d);
    },
    [ensureDay]
  );

  /** タブに戻ってきたら、開いている日を読み直す */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (!userRef.current) return;
      // 送れていない記録があれば、ここでも送っておく
      if (dirtyRef.current.size > 0) scheduleSave(true);
      reloadDay(dateStr);
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [dateStr, reloadDay, scheduleSave]);

  /**
   * ログインしたとき、この端末にだけ残っている記録を送る。
   *
   * ログインできない状態で使い続けると、記録は端末の中だけに溜まる。
   * あとでログインしても、その日を開くか編集するまで送られないので、
   * 別の端末からは見えないままになる。ここでまとめて片付ける。
   *
   * 送るのは、手元のほうが新しいものだけ。
   */
  const pushLocalBacklog = useCallback(async (uid) => {
    const dates = listLocalDates();
    log('端末に残っている日付', dates.length + '日ぶん', dates.slice(-5));
    if (dates.length === 0) return 0;

    const remoteMap = await fetchRemoteRange(uid, dates[0], dates[dates.length - 1]);

    const targets = [];
    dates.forEach((d) => {
      const local = loadLocalDay(d);
      if (local.length === 0) return;
      const remote = remoteMap[d] || [];
      const merged = mergeTasks(local, remote);
      if (needsPush(merged, remote)) targets.push([d, merged]);
    });

    // 一度に投げず、1件ずつ送る
    for (const [d, merged] of targets) {
      log('未送信ぶんを送る', d, `${alive2(merged).length}件`);
      await saveRemoteDay(uid, d, merged);
    }

    if (targets.length > 0) {
      console.info(`この端末にだけ残っていた記録を送りました: ${targets.length}日ぶん`);
    }
    return targets.length;
  }, []);

  useEffect(() => {
    if (user === undefined) return;
    const uid = user ? user.uid : null;
    if (prevUidRef.current === uid) return;
    prevUidRef.current = uid;

    migrateLocalStorage();
    loadedRef.current = new Set();
    dirtyRef.current = new Set();
    daysRef.current = {};
    setDays({});
    setReady(false);
    setHistoryState({ busy: false, done: false, error: '' });

    (async () => {
      if (user) {
        try {
          await migrateRemoteIfNeeded(user.uid);
        } catch (e) {
          console.warn('旧データの移行に失敗:', e.message);
        }
        try {
          await pushLocalBacklog(user.uid);
        } catch (e) {
          console.warn('未送信の記録を送れませんでした:', e.message);
        }
      }
      await ensureDay(toDateStr(new Date()));
      setReady(true);
    })();
  }, [user, ensureDay, pushLocalBacklog]);

  useEffect(() => {
    if (user === undefined) return;
    ensureDay(dateStr);
  }, [dateStr, user, ensureDay]);

  useEffect(() => {
    if (!user) return;
    const unsub = subscribeRemoteDay(
      user.uid,
      todayStr,
      (remote) => {
        log('当日の見張りが受信', todayStr, `${alive2(remote).length}件(全${remote.length})`);
        commit({ [todayStr]: remote }, { markDirty: false });
        if (needsPush(daysRef.current[todayStr] || [], remote)) {
          dirtyRef.current.add(todayStr);
          scheduleSave(false);
        }
      },
      (e) => {
        console.warn('同期エラー:', e.message);
        setSaveState('error');
      }
    );
    return () => unsub();
  }, [user, todayStr, commit, scheduleSave]);

  /**
   * 開いている日を見張る。
   *
   * ensureDay は一度読んだ日を loadedRef に控えるので、戻ってきても読み直さない。
   * それだと、別の端末で書き換えた内容が画面に出てこない。
   * 見ている1日だけを見張り、日付を移せば前の見張りは外れる。
   * 当日は上の useEffect が見張っているので、ここでは避ける。
   */
  useEffect(() => {
    if (!user) return;
    const target = dateStr;
    if (target === todayStr) return;

    const unsub = subscribeRemoteDay(
      user.uid,
      target,
      (remote) => {
        commit({ [target]: remote }, { markDirty: false });
        if (needsPush(daysRef.current[target] || [], remote)) {
          dirtyRef.current.add(target);
          scheduleSave(false);
        }
      },
      (e) => console.warn('同期エラー:', e.message)
    );
    return () => unsub();
  }, [user, dateStr, todayStr, commit, scheduleSave]);

  // ==========================================================
  // 過去ログの一括取得
  // ==========================================================
  const downloadHistory = useCallback(async () => {
    const u = userRef.current;
    if (!u) return;
    setHistoryState({ busy: true, done: false, error: '' });
    try {
      const from = new Date();
      from.setFullYear(from.getFullYear() - HISTORY_YEARS);
      const map = await fetchRemoteRange(u.uid, toDateStr(from), toDateStr(new Date()));
      Object.keys(map).forEach((d) => loadedRef.current.add(d));
      commit(map, { markDirty: false });
      setHistoryState({ busy: false, done: true, error: '' });
    } catch (e) {
      setHistoryState({ busy: false, done: false, error: e.message });
    }
  }, [commit]);

  // ==========================================================
  // 更新（カレンダー連携はここでだけ動く）
  // ==========================================================
  const doCalendarWork = useCallback(async (refreshing = null) => {
    // 【最適化】refreshing がある場合は、ここで確実に完了を待つ
    // これで pace() の 220ms を待たずに、直ちにトークンが有効になる
    if (refreshing) {
      try {
        await refreshing;
      } catch (e) {
        console.warn('トークンリフレッシュ失敗:', e.message);
        // エラーでも続行（古いトークンで試す、または後続の処理で対応）
      }
    }

    const s = settingsRef.current;
    const inputIds = s.inputCalendarIds || [];
    const outputId = s.outputCalendarId;
    const uid = userRef.current ? userRef.current.uid : null;
    let warning = '';

    try {
      const queue = await loadPendingDeletes(uid);
      if (queue.length > 0) {
        const rest = await flushDeletes(queue);
        if (rest.length !== queue.length) await savePendingDeletes(uid, rest);
      }
    } catch (e) {
      console.warn('削除の後始末に失敗:', e.message);
    }

    // 【最適化】importWindow と exportWindow の両方をカバーする範囲で
    // 1回だけ fetchRemoteRange を呼ぶ
    const importWin = importWindow();
    const exportWin = exportWindow();
    
    // より広い範囲でまとめて取得
    const allFromStr = importWin.fromStr < exportWin.fromStr ? importWin.fromStr : exportWin.fromStr;
    const allToStr = importWin.toStr > exportWin.toStr ? importWin.toStr : exportWin.toStr;
    
    let allWindowMap = {};
    if (uid) {
      allWindowMap = await fetchRemoteRange(uid, allFromStr, allToStr);
    }

    if (inputIds.length > 0) {
      const { fromStr, toStr } = importWindow();
      let windowMap = {};
      
      // 既に取得した allWindowMap からフィルタ
      datesBetween(fromStr, toStr).forEach((key) => {
        windowMap[key] = allWindowMap[key] || [];
      });

      datesBetween(fromStr, toStr).forEach((key) => {
        const local = daysRef.current[key] || loadLocalDay(key);
        windowMap[key] = mergeTasks(local, windowMap[key] || []);
      });

      const r = await importFromGoogle(inputIds, windowMap);
      const partial = {};
      r.changedDates.forEach((d) => {
        loadedRef.current.add(d);
        partial[d] = r.dayMap[d] || [];
      });
      commit(partial);

      // 消えたカレンダーのIDは設定から取り除く。
      // 残しておくと、画面に出ないまま毎回 404 になる。
      if (r.missingCalendarIds && r.missingCalendarIds.length > 0) {
        const cur = settingsRef.current;
        applyCalendarSettings({
          ...cur,
          inputCalendarIds: (cur.inputCalendarIds || []).filter(
            (id) => !r.missingCalendarIds.includes(id)
          ),
        });
      }
    }

    if (outputId) {
      const { fromStr, toStr } = exportWindow();
      let windowMap = {};
      
      // 既に取得した allWindowMap からフィルタ
      datesBetween(fromStr, toStr).forEach((key) => {
        windowMap[key] = allWindowMap[key] || [];
      });

      datesBetween(fromStr, toStr).forEach((key) => {
        const local = daysRef.current[key] || loadLocalDay(key);
        windowMap[key] = mergeTasks(local, windowMap[key] || []);
      });

      try {
        const r = await exportToGoogle(outputId, windowMap);
        const partial = {};
        r.changedDates.forEach((d) => {
          loadedRef.current.add(d);
          partial[d] = r.dayMap[d] || [];
        });
        commit(partial);

        // 送れなかったものがあれば知らせる。件数は出さない。
        // 残りは exportPending のままなので、次の ⟳ でまた送られる。
        if (r.failed > 0) {
          warning = '書き出しに一部失敗しました。時間をおいて再度更新してください';
        }
      } catch (e) {
        // 書き出し先ごと消えている場合は、選択を外して選び直してもらう
        if (e.code === 'output-calendar-missing') {
          const cur = settingsRef.current;
          applyCalendarSettings({ ...cur, outputCalendarId: '' });
        }
        throw e;
      }
    }

    return { warning };
  }, [commit, applyCalendarSettings]);

  const runUpdate = useCallback(() => {
    if (!isConsented()) return;

    // 【最適化】トークン期限切れなら事前にリフレッシュ開始
    // doCalendarWork 内で待つため、ここでは即座に返す
    const refreshing = refreshTokenIfNeeded();

    (async () => {
      setSyncState((prev) => ({ ...prev, busy: true, error: '' }));

      /**
       * 手動更新で書き出す対象に印を付ける。
       *
       * 送るのは、まだ送っていないものと、送ってから中身が変わったものだけ。
       *
       * 以前はここで、読み込み済みの全日付の完了タスク全部に印を付けていた。
       * 日付を行き来するほど daysRef に日が溜まるので、
       * 使っているうちに毎回100件以上を送ることになり、
       * 書き込みは220ms間隔に均されるため、それだけで20秒を超えていた。
       */
      const win = exportWindow();
      Object.keys(daysRef.current).forEach((d) => {
        if (d < win.fromStr || d > win.toStr) return;

        daysRef.current[d] = daysRef.current[d].map((t) => {
          if (!t.isCompleted || t.deletedAt) return t;

          let stale;
          if (!t.googleExportId) {
            // まだ一度も送っていない
            stale = true;
          } else if (t.exportedAt === undefined) {
            // この仕組みが入る前に送ったもの。変更があるまでは送り直さない
            stale = false;
          } else {
            stale = (t.updatedAt || 0) > t.exportedAt;
          }

          return stale ? { ...t, exportPending: true } : t;
        });
      });

      // 【最適化】refreshing を doCalendarWork に渡す
      // doCalendarWork 内で確実にトークンリフレッシュを待つ
      try {
        const r = await doCalendarWork(refreshing);
        setSyncState({
          busy: false,
          lastSyncAt: fmtHM(new Date()),
          error: (r && r.warning) || '',
        });
      } catch (e) {
        // 権限が無い場合は、つなぎ直してもらう案内を出す
        if (e.code === 'auth') setCalendarStale(true);
        setSyncState((prev) => ({ ...prev, busy: false, error: e.message }));
      }
    })();
  }, [doCalendarWork]);

  const reconnectCalendar = async () => {
    try {
      await connectGoogle();
      setCalendarStale(false);
      runUpdate();
    } catch (e) {
      console.warn('再接続に失敗:', e.message);
    }
  };

  // ==========================================================
  // 表示用
  //
  // 取り消し待ちのタスクは、並び順を保つためにリストに残しておく。
  // 表示するときに UndoRow に差し替える。
  // ==========================================================
  const undoIds = useMemo(() => new Set(Object.keys(undoMap).map(Number)), [undoMap]);

  const dayTasks = useMemo(
    () => (days[dateStr] || []).filter((t) => alive(t) || undoIds.has(t.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [days, dateStr, minuteStamp, undoIds]
  );

  const runningTasks = dayTasks.filter(
    (t) => (t.isRunning && !t.isCompleted) || (undoIds.has(t.id) && t.isRunning && !t.isCompleted)
  );

  const pendingTasks = useMemo(
    () =>
      dayTasks
        .filter((t) => !t.isRunning && !t.isCompleted)
        .sort((a, b) => {
          const av = a.scheduledStartTime || '99:99';
          const bv = b.scheduledStartTime || '99:99';
          if (av !== bv) return av.localeCompare(bv);
          return a.id - b.id;
        }),
    [dayTasks]
  );

  const completedTasks = useMemo(
    () =>
      dayTasks
        .filter((t) => t.isCompleted)
        .sort((a, b) => (a.startedAt || '').localeCompare(b.startedAt || '')),
    [dayTasks]
  );

  /** 計算には取り消し待ちを含めない */
  const liveRunning = runningTasks.filter(alive);
  const livePending = pendingTasks.filter(alive);

  // ==========================================================
  // 時間の計算（ここに書かれた式がすべて）
  //
  //   実行中の残り = 見積(分)×60 − 経過秒 → 秒を切り上げて分に。0未満は0
  //
  //   終了予定   = 起点 + 実行中の残り + 予定タスクの見積合計
  //   使える時間 = 24:00 − 終了予定
  //
  //   予定1件目の予想 = 起点 + 実行中の残り
  //   予定n件目の予想 = 予定(n-1)件目の予想 + 予定(n-1)件目の見積
  //
  //   起点は、今日なら現在時刻。未来日なら画面で指定した基準時刻。
  //
  // 開始・完了ボタンはこの式に手を加えない。
  // タスクがどの項目に属するかが変わるだけで、値は常に上の式で出し直される。
  // ==========================================================
  const baseTime = useMemo(() => {
    if (!isFuture) return new Date();
    const d = dateStrToMidnight(dateStr);
    const [h, m] = (futureBase || '00:00').split(':').map(Number);
    d.setHours(h || 0, m || 0, 0, 0);
    return d;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFuture, dateStr, futureBase, minuteStamp]);

  const runningRemainingMinutes = useMemo(
    () => liveRunning.reduce((sum, t) => sum + remainingMinutesOf(t), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [liveRunning, minuteStamp]
  );

  const pendingTotalMinutes = livePending.reduce(
    (sum, t) => sum + estToNum(t.estimatedMinutes),
    0
  );

  const totalRemainingMinutes = runningRemainingMinutes + pendingTotalMinutes;

  const endTimeStr = fmtHM(addMinutesToDate(baseTime, totalRemainingMinutes));

  const minutesFromBaseToMidnight =
    24 * 60 - (baseTime.getHours() * 60 + baseTime.getMinutes());
  const availableMinutes = minutesFromBaseToMidnight - totalRemainingMinutes;
  const availableStr = minutesToHM(availableMinutes);
  const isNegative = availableMinutes < 0;

  /**
   * 予想開始時刻を、その日の 0:00 から数えた分で返す。
   * 0時をまたぐと 1440 を超えるので、遅れの判定に使える。
   */
  const estimatedStartMinutes = (index) => {
    let acc = runningRemainingMinutes;
    for (let i = 0; i < index; i++) {
      const t = pendingTasks[i];
      if (alive(t)) acc += estToNum(t.estimatedMinutes);
    }
    const base = baseTime.getHours() * 60 + baseTime.getMinutes();
    return base + acc;
  };

  /** 予想開始時刻。取り消し待ちの行は飛ばして数える */
  const estimatedStartOf = (index) => {
    let acc = runningRemainingMinutes;
    for (let i = 0; i < index; i++) {
      const t = pendingTasks[i];
      if (alive(t)) acc += estToNum(t.estimatedMinutes);
    }
    return fmtHM(addMinutesToDate(baseTime, acc));
  };

  /**
   * 開始予定時刻に間に合わないか。
   *
   * 表示の "00:05" だけで比べると、0時をまたいだときに
   * 小さい数字になってしまい、間に合っていると誤って判定する。
   * そのため 0:00 から数えた分で比べる。
   */
  const isOverdue = (index, scheduled) => {
    if (!scheduled || isPast) return false;
    const [sh, sm] = scheduled.split(':').map(Number);
    return estimatedStartMinutes(index) > sh * 60 + sm;
  };

  /**
   * 開始予定時刻どうしの重なり。
   *
   *   前の行が実際に終わる時刻 = max(前の開始予定, 前の行が始められる時刻) + 前の見積
   *   その時刻が自分の開始予定を過ぎていたら重なりとする。
   *
   * ずれは後ろへ引き継ぐ。前が押されたせいで間に合わない行も重なりとして出す。
   * 時刻なしの行は末尾に並ぶので、そこで打ち切る。
   */
  const conflictFlags = useMemo(() => {
    const toMin = (hhmm) => {
      const [h, m] = hhmm.split(':').map(Number);
      return h * 60 + m;
    };

    const flags = [];
    let prevEnd = null;

    pendingTasks.forEach((t) => {
      if (!alive(t) || !t.scheduledStartTime || isPast) {
        flags.push(false);
        return;
      }
      const start = toMin(t.scheduledStartTime);
      const hit = prevEnd !== null && prevEnd > start;
      const actualStart = prevEnd === null ? start : Math.max(start, prevEnd);
      prevEnd = actualStart + estToNum(t.estimatedMinutes);
      flags.push(hit);
    });

    return flags;
  }, [pendingTasks, isPast]);

  // ==========================================================
  // 操作（今日のみ）
  // ==========================================================
  const touch = (t, patch) => ({ ...t, ...patch, updatedAt: Date.now() });

  const updateTask = (id, patch) =>
    updateDay(dateStr, (list) =>
      list.map((t) => {
        if (t.id !== id) return t;
        const next = touch(t, patch);
        // 実績タスク（isCompleted: true）の場合は、手動更新ボタンで同期する
        // 実行中のタスク（isCompleted: false）の場合、名前かメモが変わったらカレンダーに反映
        const changed = ['name', 'comment'].some(
          (k) => Object.prototype.hasOwnProperty.call(patch, k) && patch[k] !== t[k]
        );
        if (changed && !t.isCompleted && t.googleExportId) {
          next.exportPending = true;
        }
        return next;
      })
    );

  /**
   * 削除。
   * 押した時点でカレンダー削除の予約も入れる。
   * 取り消したときに予約を外すので、連続で削除しても取りこぼさない。
   */
  const deleteTask = (id) => {
    const target = (daysRef.current[dateStr] || []).find((t) => t.id === id);
    if (!target) return;

    // ステップ 2：削除可能な条件
    // - 当日のすべてのタスク
    // - 昨日の予定タスク（完了していないタスク）のみ
    const isYesterday = dateStr === yesterdayStr;
    const isToday = dateStr === todayStr;
    const canDelete = isToday || (isYesterday && !target.isCompleted);
    
    if (!canDelete) {
      // 削除不可の場合は何もしない
      return;
    }

    updateDay(
      dateStr,
      (list) => list.map((t) => (t.id === id ? touch(t, { deletedAt: Date.now() }) : t)),
      true
    );

    if (target.googleExportId && target.googleExportCalendarId) {
      const uid = userRef.current ? userRef.current.uid : null;
      addPendingDelete(uid, {
        calendarId: target.googleExportCalendarId,
        eventId: target.googleExportId,
        at: Date.now(),
      });
    }

    setUndoMap((prev) => ({
      ...prev,
      [id]: { name: target.name, date: dateStr, task: target },
    }));
  };

  /** 取り消しの時間が過ぎた。表示から外すだけ */
  const expireUndo = useCallback((id) => {
    setUndoMap((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const undoDelete = useCallback(
    (id) => {
      setUndoMap((prev) => {
        const item = prev[id];
        if (!item) return prev;

        updateDay(
          item.date,
          (list) => list.map((t) => (t.id === id ? touch(t, { deletedAt: null }) : t)),
          true
        );

        if (item.task.googleExportId && item.task.googleExportCalendarId) {
          const uid = userRef.current ? userRef.current.uid : null;
          removePendingDelete(uid, {
            calendarId: item.task.googleExportCalendarId,
            eventId: item.task.googleExportId,
          });
        }

        const next = { ...prev };
        delete next[id];
        return next;
      });
    },
    [updateDay]
  );

  /**
   * 実行中のタスクを止める（割り込み処理）。
   *
   *   完了ぶん … 見積 = min(元の見積, 経過分) を切り捨て   → 元の日に残る
   *   残りぶん … 見積 = max(0, 元の見積 - 経過分) を切り上げ → 今日の予定に入る
   *
   * 残りぶんの開始予定時刻は、予定タスクの先頭の1分前にする。
   * そうすると昇順で一番上に来るので、すぐ再開できる。
   * 予定が空、または先頭に時刻が入っていなければ現在時刻。
   */
  const interruptRunning = () => {
    const now = new Date();
    const ts = now.getTime();
    const nowStr = fmtHM(now);
    const today = toDateStr(now);
    const partial = {};

    const todayList = (daysRef.current[today] || []).filter(
      (t) => alive(t) && !t.isRunning && !t.isCompleted
    );
    const sorted = [...todayList].sort((a, b) => {
      const av = a.scheduledStartTime || '99:99';
      const bv = b.scheduledStartTime || '99:99';
      if (av !== bv) return av.localeCompare(bv);
      return a.id - b.id;
    });
    const head = sorted[0];
    const resumeAt =
      head && head.scheduledStartTime ? minusOneMinute(head.scheduledStartTime) : nowStr;

    Object.keys(daysRef.current).forEach((d) => {
      const list = daysRef.current[d] || [];
      const running = list.filter((t) => alive(t) && t.isRunning && !t.isCompleted);
      if (running.length === 0) return;

      let arr = [...list];

      running.forEach((t) => {
        const elapsedMs = t.startedAt ? ts - new Date(t.startedAt).getTime() : 0;
        const elapsedMin = Math.max(0, elapsedMs / 60000);

        const unset = t.estimatedMinutes === null || t.estimatedMinutes === undefined;
        const orig = estToNum(t.estimatedMinutes);

        const doneEst = unset ? null : Math.floor(Math.min(orig, elapsedMin));
        const restEst = unset ? null : Math.ceil(Math.max(0, orig - elapsedMin));

        arr = arr.map((x) =>
          x.id === t.id
            ? {
                ...x,
                isRunning: false,
                isCompleted: true,
                estimatedMinutes: doneEst,
                endedAt: now.toISOString(),
                exportPending: true,
                googleExportId: null,
                googleExportCalendarId: null,
                updatedAt: ts,
              }
            : x
        );

        const remainder = {
          ...t,
          id: newId(),
          date: today,
          isRunning: false,
          isCompleted: false,
          estimatedMinutes: restEst,
          scheduledStartTime: resumeAt,
          startedAt: null,
          endedAt: null,
          exportPending: false,
          googleExportId: null,
          googleExportCalendarId: null,
          deletedAt: null,
          updatedAt: ts,
        };

        if (today === d) arr = [...arr, remainder];
        else partial[today] = [...(partial[today] || daysRef.current[today] || []), remainder];
      });

      partial[d] = arr;
    });

    return partial;
  };

  /**
   * 昨日以前に実行中のまま残っているタスクを完了させる。
   *
   * ここは記録を書き換えるので、確かめられたときだけ動かす。
   *
   * 以前は手元の控えだけを見て判断していた。
   * 手元の控えは古いことがあり、別の端末や別のときに終わらせた記録が
   * 反映されていないと、終わっているタスクを「止め忘れ」と見なして
   * 終了時刻を今の時刻で書き潰してしまう。
   * そのため、候補があるときは Firestore と突き合わせてから決める。
   */
  const closeForgottenTasks = useCallback(async () => {
    const today = toDateStr(new Date());

    const from = new Date();
    from.setDate(from.getDate() - EXPORT_DAYS_BACK);
    const fromStr = toDateStr(from);

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const toStr = toDateStr(yesterday);
    if (toStr < fromStr) return;

    /** 止め忘れかもしれないタスク。終了時刻がすでに入っているものは含めない */
    const looksForgotten = (t) =>
      alive(t) && t.isRunning && !t.isCompleted && !t.endedAt;

    // まず手元だけを見て、候補のある日を洗い出す。
    // 候補が無ければ通信せずに終わる。ふだんはここで終わる。
    const candidateDates = datesBetween(fromStr, toStr).filter((d) => {
      if (d >= today) return false;
      const local = daysRef.current[d] || loadLocalDay(d);
      return local.some(looksForgotten);
    });

    if (candidateDates.length === 0) return;

    // 候補があるときだけ、Firestore の中身を取って突き合わせる。
    // 取れなかったときは、確かめられないので何もしない。
    const u = userRef.current;
    let remoteMap = {};
    if (u) {
      try {
        remoteMap = await fetchRemoteRange(
          u.uid,
          candidateDates[0],
          candidateDates[candidateDates.length - 1]
        );
      } catch (e) {
        console.warn('止め忘れの確認を見送りました:', e.message);
        return;
      }
    }

    const ts = Date.now();
    const partial = {};

    candidateDates.forEach((d) => {
      const local = daysRef.current[d] || loadLocalDay(d);
      const list = mergeTasks(local, remoteMap[d] || []);

      const forgotten = list.filter(looksForgotten);
      if (forgotten.length === 0) return;

      /**
       * 終了時刻は、その日の中に収める。
       * 今の時刻で閉じると、何日も経ってから押したときに
       * 実績が何百時間にもなってしまう。
       *
       *   同じ日に後から始まったタスクがあれば、その開始時刻
       *   無ければ、その日の終わり
       */
      const endOfDay = dateStrToMidnight(d);
      endOfDay.setHours(23, 59, 59, 999);

      const ids = new Set(forgotten.map((t) => t.id));

      partial[d] = list.map((t) => {
        if (!ids.has(t.id)) return t;

        const startedMs = t.startedAt ? new Date(t.startedAt).getTime() : null;

        let nextStart = null;
        list.forEach((x) => {
          if (x.id === t.id || !alive(x) || !x.startedAt) return;
          const ms = new Date(x.startedAt).getTime();
          if (isNaN(ms)) return;
          if (startedMs !== null && ms <= startedMs) return;
          if (nextStart === null || ms < nextStart) nextStart = ms;
        });

        let endMs = nextStart === null ? endOfDay.getTime() : nextStart;
        if (startedMs !== null && endMs < startedMs) endMs = startedMs;

        return {
          ...t,
          isRunning: false,
          isCompleted: true,
          endedAt: new Date(endMs).toISOString(),
          exportPending: true,
          updatedAt: ts,
        };
      });

      loadedRef.current.add(d);
    });

    if (Object.keys(partial).length > 0) {
      commit(partial, { immediate: true, replace: true });
    }
  }, [commit]);

  const startTaskRef = useRef(null);
  const completeTaskRef = useRef(null);

  const startTask = (id) => {
    const partial = interruptRunning();
    const ts = Date.now();
    const base = partial[dateStr] || daysRef.current[dateStr] || [];
    
    // 有効な scheduledStartTime を持つタスクを探す
    const tasksWithScheduledTime = base
      .filter((t) => t.id !== id && !t.isCompleted && t.scheduledStartTime)
      .filter((t) => {
        // 有効な日付形式か確認
        const time = new Date(t.scheduledStartTime).getTime();
        return !isNaN(time);
      });
    
    // 開始時刻を決定
    let startedAt;
    if (tasksWithScheduledTime.length > 0) {
      // 一番上（earliest）のタスクの scheduledStartTime の1分前
      const earliestTime = Math.min(
        ...tasksWithScheduledTime.map((t) => new Date(t.scheduledStartTime).getTime())
      );
      startedAt = new Date(earliestTime - 60 * 1000).toISOString(); // 1分前
    } else {
      // 開始時刻がないタスクしかない場合は現在時刻
      startedAt = new Date().toISOString();
    }
    
    partial[dateStr] = base.map((t) =>
      t.id === id
        ? {
            ...t,
            isRunning: true,
            isCompleted: false,
            startedAt,
            endedAt: null,
            updatedAt: ts,
          }
        : t
    );
    commit(partial, { immediate: true, replace: true });
    closeForgottenTasks();
  };

  const completeTask = (id) => {
    updateDay(
      dateStr,
      (list) =>
        list.map((t) =>
          t.id === id
            ? touch(t, {
                isRunning: false,
                isCompleted: true,
                endedAt: new Date().toISOString(),
                exportPending: true,
                comment: t.note,  // 予定メモを実績メモにコピー
              })
            : t
        ),
      true
    );
  };

  const makeTask = (name, estimatedMinutes, scheduledStartTime, d, note) => ({
    id: newId(),
    name: name || '',
    estimatedMinutes,
    scheduledStartTime: scheduledStartTime || null,
    date: d,
    // 予定のメモ（実行前の覚え書き）。完了後のメモは comment で別に持つ
    note: note || '',
    comment: '',
    startedAt: null,
    endedAt: null,
    isCompleted: false,
    isRunning: false,
    deletedAt: null,
    updatedAt: Date.now(),
  });

  startTaskRef.current = startTask;
  completeTaskRef.current = completeTask;

  const addTask = (name, est, sched, note) =>
    updateDay(dateStr, (list) => [...list, makeTask(name, est, sched, dateStr, note)], true);

  /**
   * 画面のどこにいても効くキー操作。
   *
   *   Ctrl/Cmd + K             … タスク名の欄へ移動
   *   Shift + Enter            … すぐ開始（割り込み）。名前が空でも始められる
   *   Ctrl/Cmd + Shift + Enter … 実行中があれば完了。無ければ予定の先頭を開始
   *
   * 予定への追加（Ctrl/Cmd + Enter）は、入力欄の中でだけ受ける。
   */
  const runningIdRef = useRef(null);
  const firstPendingIdRef = useRef(null);
  useEffect(() => {
    runningIdRef.current = runningTasks.length > 0 ? runningTasks[0].id : null;
    firstPendingIdRef.current = pendingTasks.length > 0 ? pendingTasks[0].id : null;
  }, [runningTasks, pendingTasks]);

  useEffect(() => {
    const onKey = (e) => {
      const action = shortcutOf(e);
      
      // ステップ 2：過去日ではショートカットキーを無効化
      // ただし、昨日の toggle（Ctrl/Cmd + Shift + Enter）のみ実行可能
      const isYesterday = dateStr === yesterdayStr;
      const isPastDate = isPast;
      
      if (isPastDate && !(isYesterday && action === 'toggle')) {
        // 昨日の toggle 以外は無効化
        return;
      }

      if (action === 'focusRunning') {
        // 実行中のタスク名欄へフォーカス
        e.preventDefault();
        // 「実行中タスク」セクションの最初の input を探す
        const taskSections = document.querySelectorAll('.task-section');
        if (taskSections.length > 0) {
          const firstSection = taskSections[0]; // 実行中タスクセクション
          const runningInput = firstSection.querySelector('input[type="text"]');
          if (runningInput) {
            runningInput.focus();
            runningInput.select();
          }
        }
        return;
      }

      if (action === 'focusAdd') {
        // タスク追加項目の入力欄へフォーカス（Ctrl+J）
        e.preventDefault();
        const el = quickNameRef.current;
        if (el) {
          el.focus();
          el.select();
        }
        return;
      }

      if (action === 'add') {
        e.preventDefault();
        const el = quickNameRef.current;
        if (el) {
          el.focus();
          el.select();
        }
        return;
      }

      if (action === 'start') {
        // 追加バーの中で押したときは、そちらが受け持つ
        const inBar = e.target && e.target.closest && e.target.closest('.quick-add-bar');
        if (inBar) return;
        if (!quickActionsRef.current) return; // 過去・未来の日は追加できない
        e.preventDefault();
        quickActionsRef.current.start();
        return;
      }

      if (action === 'toggle') {
        e.preventDefault();
        if (runningIdRef.current !== null) {
          // 実行中を完了する。次のタスクは自動では始めない
          completeTaskRef.current(runningIdRef.current);
        } else if (firstPendingIdRef.current !== null && !isPastDate) {
          // 予定の先頭を開始（過去日では実行しない）
          startTaskRef.current(firstPendingIdRef.current);
        }
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isPast, dateStr, yesterdayStr]);

  const addAndStart = (name, est, sched, note) => {
    const partial = interruptRunning();
    const t = {
      ...makeTask(name, est, sched, dateStr, note),
      isRunning: true,
      startedAt: new Date().toISOString(),
    };
    const base = partial[dateStr] || daysRef.current[dateStr] || [];
    partial[dateStr] = [...base, t];
    commit(partial, { immediate: true, replace: true });
    closeForgottenTasks();
  };

  const shiftDay = (delta) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + delta);
    setSelectedDate(d);
  };

  const handleSignOut = async () => {
    await flushSaves();
    await signOutUser();
    setShowSettings(false);
  };

  const openSettings = () => {
    refreshOnGesture();
    setShowSettings((v) => !v);
  };

  const dotClass =
    syncState.busy || saveState === 'saving'
      ? 'busy'
      : syncState.error || saveState === 'error' || calendarStale
      ? 'error'
      : user || isConsented()
      ? 'ok'
      : '';

  const showLoginBar = ready && user === null;

  /**
   * タスク1件を描く。
   * 取り消し待ちなら、その場所に UndoRow を出す。
   */
  const renderTask = (task, mode, extra = {}) => {
    if (undoMap[task.id]) {
      return (
        <UndoRow
          key={task.id}
          name={undoMap[task.id].name}
          onUndo={() => undoDelete(task.id)}
          onExpire={() => expireUndo(task.id)}
        />
      );
    }
    
    // ステップ 2：削除ボタン表示の条件
    // - 当日のすべてのタスク：readOnly=false
    // - 昨日の予定タスク（完了していないタスク）：readOnly=false
    // - その他：readOnly=true
    const isYesterday = dateStr === yesterdayStr;
    const isToday = dateStr === todayStr;
    const taskReadOnly = !(isToday || (isYesterday && !task.isCompleted)) && readOnly;
    
    return (
      <TaskRow
        key={task.id}
        task={task}
        mode={mode}
        isPast={isPast}
        readOnly={taskReadOnly}
        canEditNotes={canEditNotes}
        onOpenNote={(t) => setNoteTarget(t.id)}
        onUpdate={updateTask}
        onDelete={deleteTask}
        onStart={startTask}
        onComplete={completeTask}
        {...extra}
      />
    );
  };

  return (
    <div className="task-manager">
      <div className="header-fixed">
        <div className="header-info">
          <span className="date-display">
            {formatDateLabel(dateStr)}
            {isPast && <em className="tag">記録</em>}
            {isFuture && <em className="tag">予定</em>}
          </span>

          {!isPast && (
            <div className="header-times">
              {isFuture ? (
                <span className="current-time base-edit">
                  基準
                  <TimeInput
                    className="base-input"
                    value={futureBase}
                    onChange={(v) => setFutureBase(v || '00:00')}
                    title="この時刻から始めた場合の予測を出します（保存されません）"
                  />
                </span>
              ) : (
                <span className="current-time">
                  現在 <span className="num">{fmtHM(baseTime)}</span>
                </span>
              )}
              <span className="end-time">
                終了予定 <span className="num">{endTimeStr}</span>
              </span>
              <span className={`available-time ${isNegative ? 'negative' : ''}`}>
                使える時間 <span className="num">{availableStr}</span>
              </span>
            </div>
          )}
        </div>

        <div className="navigation">
          <button className="nav-button" onClick={() => shiftDay(-1)}>←</button>
          <button className="nav-button today-btn" onClick={() => setSelectedDate(new Date())}>
            今日
          </button>
          <button className="nav-button" onClick={() => shiftDay(1)}>→</button>
          <button className="nav-button calendar-btn" onClick={() => setShowCalendar(!showCalendar)}>
            カレンダー
          </button>
          <button className="nav-button gear" onClick={openSettings} title="設定">
            ⚙
            {dotClass && <span className={`dot ${dotClass}`} />}
          </button>
          <button
            className={`nav-button refresh ${syncState.busy ? 'busy' : ''}`}
            onClick={runUpdate}
            disabled={!isConsented() || syncState.busy}
            title="カレンダーと同期する"
          >
            ⟳
          </button>
        </div>

        {/* 追加バーもヘッダーに入れて、スクロールしても消えないようにする */}
        {!readOnly && (
          <QuickAddBar
            onStart={addAndStart}
            onAdd={addTask}
            nameRef={quickNameRef}
            actionsRef={quickActionsRef}
            onOpenNote={() => setNoteTarget('quickadd')}
          />
        )}
      </div>

      {showLoginBar && (
        <div className="login-bar">
          <span>ログインすると、記録が保存され、他の端末でも見られます</span>
          <button onClick={() => signInWithGoogle().catch(() => {})}>Googleでログイン</button>
        </div>
      )}

      {ready && user && !isConsented() && (
        <div className="login-bar subtle">
          <span>Googleカレンダーと連携すると、予定の取り込みと実績の記録ができます</span>
          <button onClick={reconnectCalendar}>連携する</button>
        </div>
      )}

      {syncState.error && (
        <div className="reconnect-bar">
          <span>カレンダーの接続を更新できませんでした</span>
          <button onClick={() => runUpdate()}>再接続</button>
        </div>
      )}

      {showCalendar && (
        <div className="calendar-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowCalendar(false);
          }
        }}>
          <div className="calendar-modal">
            <TaskCalendar
              selectedDate={selectedDate}
              onSelectDate={(d) => {
                setSelectedDate(d);
                setShowCalendar(false);
              }}
              oldestTaskDate={oldestTaskDate}
            />
          </div>
        </div>
      )}

      {isPast && (
        <div className="locked-bar">
          {canEditNotes ? (
            <>
              記録は確定しています。タスク名とメモだけ直せます
              {isYesterday && (
                <div className="locked-sub">予定タスクは削除できます</div>
              )}
            </>
          ) : (
            `この日の記録は確定しています（${EXPORT_DAYS_BACK}日より前は編集できません）`
          )}
        </div>
      )}

      {isFuture && (
        <div className="locked-bar">
          予定はGoogleカレンダーで管理します。基準時刻を変えると、その時刻から始めた場合の予測を確認できます。
        </div>
      )}

      <div className="task-section">
        <h3>実行中タスク</h3>
        {runningTasks.length === 0 ? (
          <div className="empty-note">実行中のタスクはありません</div>
        ) : (
          runningTasks.map((task) => renderTask(task, 'running'))
        )}
      </div>

      <div className="task-section">
        <h3>予定タスク</h3>
        {pendingTasks.length === 0 ? (
          <div className="empty-note">予定タスクはありません</div>
        ) : (
          pendingTasks.map((task, i) =>
            renderTask(task, 'pending', {
              estimatedStart: isPast ? null : estimatedStartOf(i),
              overdue: isOverdue(i, task.scheduledStartTime),
              conflict: conflictFlags[i],
            })
          )
        )}
      </div>

      <div className="task-section">
        <h3>完了タスク</h3>
        {completedTasks.length === 0 ? (
          <div className="empty-note">完了タスクはありません</div>
        ) : (
          completedTasks.map((task) => renderTask(task, 'done'))
        )}
      </div>

      {noteTarget === 'quickadd' && (
        <NoteModal
          title="メモ（追加するタスク）"
          value={quickActionsRef.current ? quickActionsRef.current.getNote() : ''}
          canEdit
          onChange={(v) => {
            if (quickActionsRef.current) quickActionsRef.current.setNote(v);
          }}
          onClose={() => setNoteTarget(null)}
        />
      )}

      {noteTarget !== null && noteTarget !== 'quickadd' && (() => {
        const t = dayTasks.find((x) => x.id === noteTarget);
        if (!t) return null;
        const field = t.isCompleted ? 'comment' : 'note';
        const meta = t.isCompleted
          ? [
              `見積 ${t.estimatedMinutes ?? '--'}分`,
              `実績 ${secToClock(diffSeconds(t.startedAt, t.endedAt))}`,
              `${isoToHM(t.startedAt)} ~ ${isoToHM(t.endedAt)}`,
            ]
          : [
              t.scheduledStartTime ? `開始予定 ${t.scheduledStartTime}` : '開始予定 --:--',
              `見積 ${t.estimatedMinutes ?? '--'}分`,
            ];

        return (
          <NoteModal
            title={t.isCompleted ? 'メモ（完了）' : 'メモ（予定）'}
            name={t.name}
            meta={meta}
            value={t[field] || ''}
            canEdit={canEditNotes}
            onChange={(v) => updateTask(t.id, { [field]: v })}
            onClose={() => setNoteTarget(null)}
          />
        );
      })()}

      {showSettings && (
        <SettingsModal
          settings={gcalSettings}
          onSettingsChange={applyCalendarSettings}
          syncState={syncState}
          onClose={() => setShowSettings(false)}
          user={user}
          onSignIn={signInWithGoogle}
          onSignOut={handleSignOut}
          saveState={saveState}
          onDownloadHistory={downloadHistory}
          historyState={historyState}
          tokenLive={hasLiveToken()}
        />
      )}
    </div>
  );
}

export default TaskManager;
